import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => { setTime(new Date()); }, 1000);
    return () => { clearInterval(id); };
  }, []);
  return (
    <span className="text-[11px] text-muted-foreground tabular-nums flex items-center gap-1.5">
      <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
      </span>
      <span className="sr-only">Live</span>
      {time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

/* ── Component ───────────────────────────────────────── */

interface MobileHeaderProps {
  /** Right-side action slot for page-specific actions */
  actions?: ReactNode;
}

/**
 * Mobile branding header — fixed at top.
 *
 * Top-level pages: SpecTree logo + name on left, live clock on right
 * Detail pages: chevron slides in from left, branding shifts right
 */
export function MobileHeader({ actions }: MobileHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const segments = location.pathname.split("/").filter(Boolean);
  const isDetailPage = segments.length > 1;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50",
        "bg-background/60 backdrop-blur-2xl backdrop-saturate-150",
        "border-b border-white/[0.04]",
        "pt-safe",
      )}
    >
      <div className="flex h-11 items-center justify-between px-4">
        {/* Left: animated chevron + branding */}
        <div className="flex items-center min-w-0">
          <AnimatePresence initial={false}>
            {isDetailPage && (
              <motion.button
                key="back"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 44, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                onClick={() => { void navigate(-1); }}
                aria-label="Go back"
                className="flex items-center justify-center h-11 w-11 shrink-0 rounded-full text-muted-foreground active:bg-white/[0.08] transition-colors overflow-hidden -ml-2"
              >
                <ChevronLeft className="h-5 w-5 shrink-0" />
              </motion.button>
            )}
          </AnimatePresence>
          <div className="flex items-center gap-2">
            <img src="/toro-logo.svg" alt="SpecTree" className="h-5 shrink-0" />
            <span className="text-sm font-semibold text-foreground font-display">
              SpecTree
            </span>
          </div>
        </div>

        {/* Right: actions + live clock */}
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          <LiveClock />
        </div>
      </div>
    </header>
  );
}
